/**
 * SyncManager — global singleton that manages offline queue and sync status.
 *
 * Responsibilities:
 * 1. Maintain a queue of failed API operations (IndexedDB or localStorage)
 * 2. Monitor navigator.onLine status
 * 3. Flush queued operations when connection is restored
 * 4. Broadcast sync status changes to all useSyncedState hooks
 * 5. Handle conflict resolution (last-write-wins by default)
 */

import { ONBOARDIX_TOKEN_KEY, ONBOARDIX_USER_KEY } from '../utils/storageKeys'

const QUEUE_KEY = 'onboardix_sync_queue'
const MAX_RETRIES = 5
const RETRY_DELAYS = [1000, 3000, 10000, 30000, 60000] // progressive backoff
// Stale ops dropped on load — без лимита очередь от старых релизов растёт
// в localStorage и держит SyncStatusBar в состоянии "Ожидание синхронизации"
// бесконечно. 24ч — верхняя граница разумного офлайна.
const STALE_OP_MS = 24 * 60 * 60 * 1000
const MAX_QUEUE_SIZE = 500
// Replay fetch budget. Без таймаута raw fetch на half-open соединении
// (captive portal / сервер принял запрос, но не отвечает) висит вечно —
// flushQueue никогда не резолвится, _isFlushing застревает в true, и индикатор
// замирает на «Ожидание синхронизации N» навсегда. 30с — как дефолт apiFetch.
const REPLAY_TIMEOUT_MS = 30000

class SyncManager {
    constructor() {
        this._queue = this._loadQueue()
        this._resources = new Map()
        this._listeners = new Set()
        this._isOnline = navigator.onLine
        this._isFlushing = false
        this._retryTimer = null
        // Операции, которые уже не будут отправлены (4xx или исчерпан бюджет).
        this._dropped = []

        // Listen for online/offline events
        window.addEventListener('online', () => this._handleOnline())
        window.addEventListener('offline', () => this._handleOffline())

        // Periodic check for queued items
        setInterval(() => this._checkQueue(), 30000)


    }

    // ── Public API ──

    get isOnline() { return this._isOnline }
    get pendingCount() { return this._queue.length }
    get droppedCount() { return this._dropped.length }
    /** Что именно не доехало — для подсказки человеку. */
    get droppedOps() { return [...this._dropped] }
    get status() {
        if (this._isFlushing) return 'syncing'
        if (!this._isOnline) return 'offline'
        if (this._queue.length > 0) return 'pending'
        // «Синхронизировано» при потерянной операции — прямая ложь: человеку
        // уже сказали «сохранено и отправится само», а оно не отправилось.
        if (this._dropped.length > 0) return 'failed'
        return 'synced'
    }

    /** Человек увидел сообщение о потере — гасим отметку. */
    clearDropped() {
        if (!this._dropped.length) return
        this._dropped = []
        this._notifyListeners()
    }

    /**
     * Register a resource managed by useSyncedState.
     */
    registerResource(key, handlers) {
        this._resources.set(key, handlers)
    }

    unregisterResource(key) {
        this._resources.delete(key)
    }

    /**
     * Enqueue a failed operation for later retry.
     *
     * `operation.dedupeKey` — необязательный отпечаток ЛОГИЧЕСКОГО действия
     * (у нас — метод + адрес + ключ повторной отправки). Пока связи нет, человек
     * жмёт кнопку ещё раз, и без склейки очередь копила по операции на нажатие:
     * после восстановления связи все они проигрывались подряд и давали пачку
     * одинаковых дел. Повтор с тем же отпечатком ЗАМЕЩАЕТ прежнюю запись —
     * тело и токен берутся свежие, место в очереди сохраняется.
     *
     * Операции без `dedupeKey` не склеиваются никогда: два разных дела могут
     * отличаться только телом запроса, и слить их означало бы потерять одно.
     */
    enqueue(operation) {
        const dedupeKey = operation?.dedupeKey || null
        if (dedupeKey) {
            const existing = this._queue.find(op => op.dedupeKey === dedupeKey)
            if (existing) {
                existing.body = operation.body ?? existing.body
                existing.headers = operation.headers || existing.headers
                existing.authHeader = operation.authHeader || existing.authHeader
                existing.timestamp = Date.now()
                // Повтор — это НОВАЯ попытка человека, а не продолжение старой:
                // счётчик ретраев и срок жизни начинаются заново. Иначе запись,
                // уже добившая бюджет фоновыми повторами, выбрасывалась бы на
                // первом же отказе — при том что человеку показано «сохранено».
                existing.retries = 0
                existing.createdAt = Date.now()
                this._saveQueue()
                this._notifyListeners()
                return
            }
        }
        const entry = {
            ...operation,
            id: `${operation.key}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            retries: 0,
            createdAt: Date.now(),
        }
        this._queue.push(entry)
        this._saveQueue()
        this._notifyListeners()

    }

    /**
     * Flush all queued operations (called when backend becomes available).
     *
     * Each op in the queue must have:
     *   { url, method, body?, authHeader? } — full HTTP spec to replay.
     *
     * Legacy ops with only { key, type: 'save' } are skipped (no-op).
     */
    async flushQueue() {
        if (this._isFlushing || this._queue.length === 0) return
        this._isFlushing = true
        this._notifyListeners()

        const failedOps = []
        const queuedBefore = this._queue.length

        try {
        for (const op of [...this._queue]) {
            try {
                if (op.url && op.method) {
                    // ── Modern path: replay the original HTTP request ──
                    // Заголовки исходного запроса переигрываются как есть —
                    // среди них `Idempotency-Key`, по которому сервер отличает
                    // повтор от нового действия. Без него отложенная отправка
                    // создавала второе дело поверх уже сохранённого.
                    const headers = { 'Content-Type': 'application/json', ...(op.headers || {}) }
                    // Токен берём свежий — но ТОЛЬКО если за клавиатурой тот же
                    // человек, что завёл действие. Сохранённый в очереди токен за
                    // часы офлайна протухает и даёт 401, поэтому обновлять его
                    // нужно; но очередь переживает выход и вход другого
                    // сотрудника на той же машине, и слепое обновление оформило
                    // бы чужое дело на него — вместе с записью в журнале.
                    const auth = this._replayAuthHeader(op)
                    if (auth) headers['Authorization'] = auth

                    const fetchOpts = { method: op.method, headers }
                    if (op.body && op.method !== 'GET') fetchOpts.body = op.body

                    // Bound the replay — a hung connection must NOT freeze the queue.
                    const controller = new AbortController()
                    const timeoutId = setTimeout(() => controller.abort(), REPLAY_TIMEOUT_MS)
                    let resp
                    try {
                        resp = await fetch(op.url, { ...fetchOpts, signal: controller.signal })
                    } finally {
                        clearTimeout(timeoutId)
                    }

                    if (!resp.ok) {
                        // 4xx = permanent failure (bad request, gone) — drop it
                        if (resp.status >= 400 && resp.status < 500) {
                            console.error(`[SyncManager] ✗ Dropping op (${resp.status}): ${op.method} ${op.url}`)
                            this._recordDropped(op, `HTTP ${resp.status}`)
                            continue
                        }
                        // 5xx = server error — schedule retry
                        throw new Error(`Server error ${resp.status}`)
                    }
                    console.info(`[SyncManager] ✓ Replayed: ${op.method} ${op.url}`)
                } else {
                    // Legacy op — skip silently (no HTTP spec available)
                    console.warn(`[SyncManager] Skipping legacy op (no url): ${op.key}`)
                }
            } catch (err) {
                op.retries = (op.retries || 0) + 1
                if (op.retries < MAX_RETRIES) {
                    failedOps.push(op)
                    console.warn(`[SyncManager] ✗ Retry ${op.retries}/${MAX_RETRIES}: ${op.method || op.key} ${op.url || ''}`, err.message)
                } else {
                    console.error(`[SyncManager] ✗ Max retries reached, dropping: ${op.method || op.key} ${op.url || ''}`)
                    this._recordDropped(op, 'исчерпаны попытки')
                }
            }
        }

            this._queue = failedOps
            this._saveQueue()
        } finally {
            // Always release the flush lock — otherwise a single hung/throwing
            // replay would block every future flush and freeze the indicator.
            this._isFlushing = false
            this._notifyListeners()
        }

        // After successful flush — refresh all page data from API.
        // Считаем от размера очереди ДО прохода. Прежнее условие сравнивало
        // `failedOps` с уже перезаписанной очередью (`this._queue === failedOps`),
        // то есть срабатывало РОВНО НАОБОРОТ: экраны перечитывались после неудач
        // и молчали после успеха. Отправленное из очереди дело появлялось на
        // вкладке только после перезагрузки страницы.
        if (failedOps.length < queuedBefore) {
            for (const [key, resource] of this._resources) {
                if (resource?.loadFromApi) {
                    resource.loadFromApi().catch(err =>
                        console.warn(`[SyncManager] Failed to refresh ${key} after flush:`, err.message)
                    )
                }
            }
        }

        // Schedule retry for remaining failed ops
        if (failedOps.length > 0) {
            const delay = RETRY_DELAYS[Math.min(failedOps[0].retries - 1, RETRY_DELAYS.length - 1)]
            this._scheduleRetry(delay)
        }
    }

    /**
     * Subscribe to sync status changes.
     */
    subscribe(listener) {
        this._listeners.add(listener)
        return () => this._listeners.delete(listener)
    }

    /**
     * Clear all queued operations (manual reset).
     */
    clearQueue() {
        this._queue = []
        this._saveQueue()
        this._notifyListeners()
    }

    /**
     * Remove queued operations by predicate.
     */
    removeWhere(predicate) {
        const before = this._queue.length
        this._queue = this._queue.filter(op => {
            try {
                return !predicate(op)
            } catch {
                return true
            }
        })
        if (this._queue.length !== before) {
            this._saveQueue()
            this._notifyListeners()
        }
    }

    // ── Internal ──

    _handleOnline() {

        this._isOnline = true
        this._notifyListeners()

        // Flush queued operations
        setTimeout(() => this.flushQueue(), 1000) // small delay to ensure connection is stable

        // Refresh all registered resources
        for (const [key, resource] of this._resources) {
            if (resource.loadFromApi) {
                resource.loadFromApi().catch(err => {
                    console.warn(`[SyncManager] Failed to refresh ${key}:`, err.message)
                })
            }
        }
    }

    _handleOffline() {

        this._isOnline = false
        this._notifyListeners()
    }

    _checkQueue() {
        if (this._isOnline && this._queue.length > 0 && !this._isFlushing) {
            this.flushQueue()
        }
    }

    _scheduleRetry(delay) {
        if (this._retryTimer) clearTimeout(this._retryTimer)
        this._retryTimer = setTimeout(() => {
            if (this._isOnline) this.flushQueue()
        }, delay)
    }

    /** Текущий заголовок авторизации из хранилища; null — если не вошли. */
    _currentAuthHeader() {
        try {
            const token = localStorage.getItem(ONBOARDIX_TOKEN_KEY)
            return token ? `Bearer ${token}` : null
        } catch {
            return null
        }
    }

    /** Кто сейчас в системе на этой машине; null — если не вошли. */
    _currentUserId() {
        try {
            return JSON.parse(localStorage.getItem(ONBOARDIX_USER_KEY))?.id ?? null
        } catch {
            return null
        }
    }

    /**
     * Заголовок авторизации для отправки отложенной операции.
     *
     * Свежий токен — только когда в системе ТОТ ЖЕ человек, что завёл действие.
     * Сменился человек — едем под его собственным сохранённым токеном; если тот
     * протух, сервер ответит 401, и операция честно попадёт в «не отправлено»,
     * а не оформится на чужое имя.
     *
     * Записи без метки автора (положенные до этого релиза, живут ≤24 ч) тоже
     * идут под сохранённым токеном: раз потеря стала видна человеку, честный
     * 401 лучше, чем догадка о том, чья это работа.
     */
    _replayAuthHeader(op) {
        const authorId = op.userId ?? null
        if (authorId !== null && authorId === this._currentUserId()) {
            return this._currentAuthHeader() || op.authHeader || null
        }
        return op.authHeader || this._currentAuthHeader() || null
    }

    _recordDropped(op, reason) {
        this._dropped.push({ method: op.method || '', url: op.url || '', reason })
    }

    _notifyListeners() {
        const status = {
            isOnline: this._isOnline,
            pendingCount: this._queue.length,
            droppedCount: this._dropped.length,
            droppedOps: [...this._dropped],
            isFlushing: this._isFlushing,
            status: this.status,
        }
        for (const listener of this._listeners) {
            try { listener(status) }
            catch { /* ignore */ }
        }
    }

    _loadQueue() {
        try {
            const raw = localStorage.getItem(QUEUE_KEY)
            if (!raw) return []
            const parsed = JSON.parse(raw)
            if (!Array.isArray(parsed)) return []
            const now = Date.now()
            const fresh = parsed.filter(op => {
                const ts = op?.createdAt || op?.timestamp || 0
                return ts && (now - ts) < STALE_OP_MS
            })
            const trimmed = fresh.length > MAX_QUEUE_SIZE
                ? fresh.slice(-MAX_QUEUE_SIZE)
                : fresh
            if (trimmed.length !== parsed.length) {
                try { localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed)) } catch { /* ignore */ }
                console.info(`[SyncManager] Dropped ${parsed.length - trimmed.length} stale/overflow ops on load`)
            }
            return trimmed
        } catch {
            return []
        }
    }

    _saveQueue() {
        try {
            localStorage.setItem(QUEUE_KEY, JSON.stringify(this._queue))
        } catch {
            console.warn('[SyncManager] Failed to persist queue')
        }
    }
}

// Singleton instance
export const syncManager = new SyncManager()
